const PROMPTED_LRU = 20;

const itemPromptText = (item) => {
  const prompt = item?.prompt;
  if (typeof prompt === 'string') return prompt;
  if (prompt && typeof prompt.p === 'string') return prompt.p;
  return '';
};

const itemMatchesCache = (item, media, hd, prompt) => {
  if (!item) return false;
  if (media === 'images' && item.media_kind === 'video') return false;
  if (media === 'videos' && item.media_kind !== 'video') return false;
  if (hd === 'hires' && !item.is_hd) return false;
  if (hd === 'lowres' && item.is_hd) return false;
  const needle = String(prompt || '').trim().toLowerCase();
  if (!needle) return true;
  return itemPromptText(item).toLowerCase().includes(needle);
};

const compareGalleryItems = (left, right) => {
  const byTime = Date.parse(right?.updated_at || '') - Date.parse(left?.updated_at || '');
  if (byTime !== 0 && Number.isFinite(byTime)) return byTime;
  const byKind = String(right?.media_kind || '').localeCompare(String(left?.media_kind || ''));
  if (byKind !== 0) return byKind;
  return Number(right?.job_id || 0) - Number(left?.job_id || 0);
};

const cloneItems = (items) => (Array.isArray(items) ? items.map((item) => ({ ...item })) : []);

export const galleryListCacheKey = (media, hd, prompt) => {
  const mediaKey = ['images', 'videos', 'both'].includes(media) ? media : 'both';
  const hdKey = ['lowres', 'hires', 'both'].includes(hd) ? hd : 'both';
  return `${mediaKey}\t${hdKey}\t${String(prompt || '').trim()}`;
};

export const createGalleryListCache = () => {
  const entries = new Map();
  const promptedOrder = [];

  const parseKey = (key) => {
    const [media = 'both', hd = 'both', prompt = ''] = String(key).split('\t');
    return { media, hd, prompt };
  };

  const touchPrompted = (key) => {
    const { prompt } = parseKey(key);
    if (!prompt) return;
    const index = promptedOrder.indexOf(key);
    if (index >= 0) promptedOrder.splice(index, 1);
    promptedOrder.push(key);
    while (promptedOrder.length > PROMPTED_LRU) {
      const evict = promptedOrder.shift();
      if (evict) entries.delete(evict);
    }
  };

  const get = (media, hd, prompt) => {
    const key = galleryListCacheKey(media, hd, prompt);
    const entry = entries.get(key);
    if (!entry) return null;
    touchPrompted(key);
    return {
      items: cloneItems(entry.items),
      nextCursor: entry.nextCursor || null,
      totalCount: entry.totalCount,
    };
  };

  const put = (media, hd, prompt, { items, nextCursor, totalCount } = {}) => {
    const key = galleryListCacheKey(media, hd, prompt);
    entries.set(key, {
      items: cloneItems(items),
      nextCursor: nextCursor || null,
      totalCount: Number.isFinite(Number(totalCount)) ? Number(totalCount) : cloneItems(items).length,
    });
    touchPrompted(key);
  };

  const append = (media, hd, prompt, { items, nextCursor, totalCount } = {}) => {
    const key = galleryListCacheKey(media, hd, prompt);
    const prev = entries.get(key);
    if (!prev) {
      put(media, hd, prompt, { items, nextCursor, totalCount });
      return;
    }
    const seen = new Set(prev.items.map((item) => item.output_key));
    cloneItems(items).forEach((item) => {
      if (!item?.output_key || seen.has(item.output_key)) return;
      seen.add(item.output_key);
      prev.items.push(item);
    });
    prev.nextCursor = nextCursor || null;
    if (Number.isFinite(Number(totalCount))) prev.totalCount = Number(totalCount);
    touchPrompted(key);
  };

  const removeItem = (outputKey) => {
    if (!outputKey) return;
    entries.forEach((entry) => {
      const before = entry.items.length;
      entry.items = entry.items.filter((item) => item.output_key !== outputKey);
      const removed = before - entry.items.length;
      if (removed > 0) entry.totalCount = Math.max(0, entry.totalCount - removed);
    });
  };

  const upsertItem = (item) => {
    if (!item?.output_key) return;
    entries.forEach((entry, key) => {
      const { media, hd, prompt } = parseKey(key);
      const shouldBe = itemMatchesCache(item, media, hd, prompt);
      const index = entry.items.findIndex((row) => row.output_key === item.output_key);
      if (shouldBe && index >= 0) {
        entry.items[index] = { ...item };
        return;
      }
      if (shouldBe) {
        entry.items.push({ ...item });
        entry.items.sort(compareGalleryItems);
        entry.totalCount += 1;
        return;
      }
      if (index >= 0) {
        entry.items.splice(index, 1);
        entry.totalCount = Math.max(0, entry.totalCount - 1);
      }
    });
  };

  const clear = () => {
    entries.clear();
    promptedOrder.length = 0;
  };

  return { get, put, append, removeItem, upsertItem, clear };
};
